import { useNuxtApp } from 'nuxt/app'
import { sanitizeAutoLogoutRequestUrl } from '@common/analytics-layer/tracking/auto-logout-envelope'
import { addRequestMetadata, clearLatencyMetrics, getLatencyMetrics, trackApiLatency } from '../utils/api-stats.utils'
import { resolveRequestAuth } from '../utils/resolve-request-auth'

import { getConfig } from '../../core-layer/config'
import { useAuthStore } from '../../auth-layer/store/auth.store'
import { useGlobal } from '../../core-layer/composables/useGlobal'
import { useLogger } from '../../core-layer/composables/useLogger'
import { useLocationStore } from '../../location-layer/store/location.store'
import { hasAdminV2PreviewSession } from '../../core-layer/utils/admin-preview-token.utils'
import type { SsrAuthEvent } from '../../core-layer/utils/cookie.utils'
import type { Pinia } from 'pinia'

// Define proper types for fetch operations (aligned with $fetch / Nitro body: no bare number/boolean)
type RequestBody = string | FormData | URLSearchParams | Record<string, unknown> | unknown[] | Blob | ArrayBuffer | null

export interface FetchRequestInit {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  headers?: Record<string, string>
  body?: RequestBody
  baseURL?: string
  timeout?: number
  retry?: number
  params?: Record<string, string | number | boolean>
  [key: string]: unknown
}

export interface FetchResponse<T = unknown> {
  data: T
  status: number
  statusText: string
  headers: Record<string, string>
  url: string
}

export interface FetchError {
  data?: {
    message: string
    statusCode: number
    error: string
    [key: string]: unknown
  }
  response?: {
    headers?: Record<string, string>
    [key: string]: unknown
  }
  message: string
  status?: number
  statusText?: string
}

// Define our custom fetch options interface
export interface CustomFetchOptions extends FetchRequestInit {
  url?: string
  groupId?: string
  locationId?: string
  productId?: string
  /** When true, getFullURL uses only baseURL + endpoint (no basePath). Used e.g. for /documents/download/:id. */
  skipBasePath?: boolean
  /**
   * When true, bypass the `/clientportal-middleware/` URL rewriting in `handleClientPortalRequests`.
   * Used for endpoints that must hit the upstream services directly (e.g. members export),
   * not for standard Client Portal clientclub traffic.
   */
  skipClientPortalMiddleware?: boolean
  /** When 'blob', use raw response and return { data: Blob, headers }. Used by getBlob. */
  responseType?: 'blob'
  metadata?: {
    groupId?: string
    locationId?: string
    productId?: string
    [key: string]: unknown
  }
}

// Define fetch instance type
export type FetchInstance = (url: string, options?: CustomFetchOptions) => Promise<unknown>

/**
 * Structural subset of `ReturnType<typeof useNuxtApp>` that BaseService needs for
 * server-side contexts where `useNuxtApp()` is not reachable (e.g., nitro handlers
 * called outside Vue/Nuxt composable setup). Callers capture the real nuxtApp
 * earlier in the request lifecycle and pass it in via `BaseServiceConfig.nuxtApp`.
 */
export interface SSRNuxtAppLike {
  $pinia?: Pinia
  ssrContext?: { event?: SsrAuthEvent } | null
  /** Shared single-flight slot for 401 refresh dedup on this request's nuxtApp. */
  _refreshInFlight?: Promise<boolean>
}

export interface BaseServiceConfig {
  baseURL?: string
  basePath?: string
  headers?: Record<string, string>
  serviceName?: string // Service name for logging (avoids minified constructor.name)
  /**
   * Pre-resolved nuxtApp for SSR contexts where `useNuxtApp()` is not reachable
   * (e.g., nitro server handlers invoked outside composable setup). When provided,
   * `prepareRequest` uses `nuxtApp.$pinia` and `tryRefreshAccessToken` uses
   * `nuxtApp._refreshInFlight` for single-flight dedup whenever the ambient
   * `useNuxtApp()` throws. No effect on client-side rendering.
   */
  nuxtApp?: SSRNuxtAppLike
  interceptors?: {
    request?: (options: CustomFetchOptions) => CustomFetchOptions | Promise<CustomFetchOptions>
    response?: {
      success?: (response: FetchResponse) => FetchResponse
      error?: (error: FetchError) => FetchError | Promise<FetchError>
    }
  }
}

// Default API response wrapper type
export interface ApiResponse<T = unknown> {
  data: T
  success?: boolean
  message?: string
  error?: string
}

// Type definitions for headers
interface HTTPHeaders {
  channel: string
  source: string
  version: string
  'x-app-version': string
  'x-app-build': string
  'x-location-id'?: string
  'x-group-id'?: string
  'x-product-id'?: string
  [key: string]: string | undefined
}

/**
 * Base service class that all API services should extend
 * Combines HTTP functionality with URL building and configuration
 *
 * @example
 * ```typescript
 * // Simple usage - extends BaseService directly
 * class PostsService extends BaseService {
 *   constructor() {
 *     super({
 *       baseURL: 'COMMUNITIES_SERVER_URL',
 *       basePath: '/posts'
 *     })
 *   }
 *
 *   async getPosts() {
 *     const { data } = await this.get<Post[]>('/posts')
 *     return data
 *   }
 * }
 * ```
 */
export abstract class BaseService {
  protected config: BaseServiceConfig
  private static clientInstance: FetchInstance | null = null
  private static serverInstance: FetchInstance | null = null
  protected logger = useLogger().logger

  constructor(config: BaseServiceConfig = {}) {
    this.config = config
  }

  public setHeaders(headers: Record<string, string>) {
    this.config.headers = { ...this.config.headers, ...headers }
  }

  /**
   * Reset the axios instances - useful for testing or reconfiguration
   */
  public static resetInstances(): void {
    BaseService.clientInstance = null
    BaseService.serverInstance = null
  }

  /**
   * Get API latency metrics summary
   * Useful for debugging and performance analysis
   */
  public static getLatencyMetrics() {
    return getLatencyMetrics()
  }

  /**
   * Clear latency metrics
   */
  public static clearLatencyMetrics(): void {
    clearLatencyMetrics()
  }

  /**
   * Get the shared fetch instance
   * Uses separate instances for client and server to ensure proper configuration.
   *
   * When a `nuxtApp` was supplied in the config (SSR-only fallback), the returned
   * function stamps it onto `options.metadata._ssrNuxtApp` so the static
   * `prepareRequest` / `tryRefreshAccessToken` can use it when the ambient
   * `useNuxtApp()` is not reachable.
   */
  protected get http(): FetchInstance {
    let instance: FetchInstance

    // Use separate instances for client and server
    if (process.server) {
      if (!BaseService.serverInstance) {
        BaseService.serverInstance = BaseService.createFetchInstance()
      }
      instance = BaseService.serverInstance
    } else {
      if (!BaseService.clientInstance) {
        BaseService.clientInstance = BaseService.createFetchInstance()
      }
      instance = BaseService.clientInstance
    }

    // Apply service-specific interceptors if any
    if (this.config.interceptors) {
      this.applyServiceInterceptors(instance)
    }

    // Thread an SSR-provided nuxtApp through per-request metadata so the shared
    // static fetch can fall back to it when `useNuxtApp()` throws on SSR.
    const ssrNuxtApp = this.config.nuxtApp
    if (!ssrNuxtApp) {
      return instance
    }

    const sharedInstance = instance
    return (url: string, options: CustomFetchOptions = {}) => {
      const merged: CustomFetchOptions = {
        ...options,
        metadata: {
          ...(options.metadata || {}),
          _ssrNuxtApp: ssrNuxtApp,
        },
      }
      return sharedInstance(url, merged)
    }
  }

  /**
   * Apply service-specific interceptors
   */
  private applyServiceInterceptors(instance: FetchInstance): void {
    if (!this.config.interceptors)
      return // Note: ofetch doesn't have the same interceptor pattern as axios
      // Service-specific interceptors would need to be handled differently
      // For now, we'll store them for potential future use
    ;(
      instance as FetchInstance & {
        _serviceInterceptors?: BaseServiceConfig['interceptors']
      }
    )._serviceInterceptors = this.config.interceptors
  }

  /**
   * Prepare request by adding metadata, auth, and other headers
   */
  private static prepareRequest(requestUrl: string, options: CustomFetchOptions): { finalUrl: string; finalOptions: CustomFetchOptions } {
    // Add request metadata for timing
    addRequestMetadata(options)

    // Ensure headers object exists
    if (!options.headers) {
      options.headers = {}
    }

    // Check if groupId and locationId are provided in metadata (from feature services)
    const groupIdFromService = options.groupId || options.metadata?.groupId
    const locationIdFromService = options.locationId || options.metadata?.locationId
    const productIdFromService = options.productId || options.metadata?.productId

    // For GoKollab platform, prioritize service-provided IDs
    if (groupIdFromService) {
      options.headers['x-group-id'] = groupIdFromService
    }

    if (locationIdFromService) {
      options.headers['x-location-id'] = locationIdFromService
    }

    if (productIdFromService) {
      options.headers['x-product-id'] = productIdFromService
    }

    // Skip token for specific endpoints
    if (BaseService.shouldSkipToken(requestUrl)) {
      return { finalUrl: requestUrl, finalOptions: options }
    }

    try {
      // Check if we're in a proper Nuxt context before accessing Pinia.
      // On SSR outside composable setup (e.g. nitro handlers) `useNuxtApp()` throws;
      // fall back to an SSR-provided nuxtApp threaded via `options.metadata._ssrNuxtApp`
      // (set by BaseService#http when `config.nuxtApp` was supplied).
      let pinia: Pinia | null = null
      try {
        const nuxtApp = useNuxtApp()
        pinia = nuxtApp.$pinia as Pinia
      } catch {
        const ssrNuxtApp = (options.metadata?._ssrNuxtApp as SSRNuxtAppLike | undefined) ?? undefined
        if (ssrNuxtApp?.$pinia) {
          pinia = ssrNuxtApp.$pinia
        } else {
          // Not in a proper Nuxt context and no SSR fallback was provided.
          const { logger } = useLogger()
          logger.warn('Not in Nuxt context, skipping auth token', {
            labels: {
              context: 'BaseService.prepareRequest',
              url: requestUrl,
            },
          })
        }
      }

      const authStore = useAuthStore(pinia)
      const traceContext = crypto.randomUUID()
      options.headers['x-parent-trace-context'] = traceContext
      options.headers['x-trace-context'] = traceContext

      // A caller-supplied Authorization header is always authoritative — notably
      // `AuthService.loginWithAdminTokenV2`, which sends the fresh agency Bearer as an explicit
      // header on `/user/v2/login-token` (a `/membership`-prefixed URL that would otherwise match
      // the admin-preview branch below). Without this, a stale `acatv2` session would make
      // resolveRequestAuth overwrite that fresh token with the old admin token. Uses the
      // pre-mutation snapshot (`_hasCallerExplicitAuthHeader`) rather than `options.headers`
      // directly because this also runs on the 401-retry pass, where `.Authorization` would be
      // BaseService's own first-pass value, not the caller's.
      const hasExplicitAuthHeader = Boolean(options.metadata?._hasCallerExplicitAuthHeader)

      // Courses admin preview (IAM v2): if a valid `acatv2` admin session exists (client-only
      // sessionStorage) for a courses/membership call, attach the admin token + server-minted
      // location scope instead of the learner token (mirrors membership-hmi-preview
      // `applyAdminV2PreviewAuth`); `handleClientPortalRequests` proxies these through
      // clientportal-middleware. SSR / non-admin requests keep the learner path below.
      const adminAuth = resolveRequestAuth(requestUrl)
      if (hasExplicitAuthHeader) {
        // Leave the caller's header untouched; still attach the location scope for admin requests.
        if (adminAuth.isAdmin && adminAuth.locationId) {
          options.headers['x-location-id'] = adminAuth.locationId
        }
      } else if (adminAuth.isAdmin && adminAuth.token) {
        options.headers.Authorization = `Bearer ${adminAuth.token}`
        if (adminAuth.locationId) {
          options.headers['x-location-id'] = adminAuth.locationId
        }
      } else if (authStore.isAuthenticated && authStore.token) {
        const token = authStore.token
        options.headers.Authorization = `Bearer ${token}`
        // options.headers['token-id'] = token // a few endpoints need this like comments, media/gif ,etc
      }
    } catch (error) {
      const { logger } = useLogger()
      logger.error('Auth store not available', {
        labels: {
          context: 'BaseService.prepareRequest',
          url: requestUrl,
        },
        error: error instanceof Error ? error : new Error(String(error)),
      })
    }

    // Handle GoKollab, Meet, or ClientPortal specific request modifications
    try {
      const { isGokollab, isMeet, isClientportal } = useGlobal()

      let finalUrl = requestUrl
      let finalOptions = options

      if (isGokollab) {
        const result = BaseService.handleGoKollabRequests(requestUrl, options)
        finalUrl = result.finalUrl
        finalOptions = result.finalOptions
      } else if (isMeet) {
        const result = BaseService.handleMeetAppRequest(requestUrl, options)
        finalUrl = result.finalUrl
        finalOptions = result.finalOptions
      } else if (isClientportal) {
        const result = BaseService.handleClientPortalRequests(requestUrl, options)
        finalUrl = result.finalUrl
        finalOptions = result.finalOptions
      }

      return { finalUrl, finalOptions }
    } catch (error) {
      // If we can't get the global state, fall back to original behavior
      const { logger } = useLogger()
      logger.warn('Could not access global state, using default request handling', {
        labels: {
          context: 'BaseService.prepareRequest',
          url: requestUrl,
        },
        error: error instanceof Error ? error : new Error(String(error)),
      })

      return { finalUrl: requestUrl, finalOptions: options }
    }
  }

  /**
   * Handle response middleware
   */
  private static handleResponse(response: unknown, config?: CustomFetchOptions): unknown {
    // Track API latency for server-side requests
    if (config && typeof config === 'object') {
      trackApiLatency(config, 200, null)
    }
    return response
  }

  /**
   * Handle response error middleware
   */
  private static async handleResponseError(response: unknown, error: FetchError, config?: CustomFetchOptions): Promise<never> {
    // Track API latency for failed requests with enhanced error details
    if (config && typeof config === 'object') {
      try {
        const errorDetails = {
          requestUrl: config.url || 'unknown',
          requestHeaders: (config.headers as Record<string, string>) || {},
          responseData: error.data || null,
          responseHeaders: error.response?.headers || {},
        }
        trackApiLatency(config, error.status || 500, error.message || 'Unknown error', errorDetails)
      } catch (trackingError) {
        // Don't let tracking errors crash the main error handling
        const { logger } = useLogger()
        logger.warn('Failed to track API latency for error', {
          labels: {
            context: 'BaseService.handleResponseError',
            originalError: error.message || 'Unknown error',
          },
          error: trackingError instanceof Error ? trackingError : new Error(String(trackingError)),
        })
      }
    }

    return Promise.reject(error)
  }

  private static toFetchError(error: unknown): FetchError {
    return {
      message: BaseService.extractErrorMessage(error),
      data: (error as FetchError).data || undefined,
      response: (error as FetchError).response || undefined,
      status: (error as FetchError).status || 500,
      statusText: (error as FetchError).statusText || 'Internal Server Error',
    }
  }

  /**
   * Run $fetch / $fetch.raw for prepared options (blob vs JSON) and attach apiCallDuration metadata.
   */
  private static async executePreparedFetch(
    finalUrl: string,
    finalOptions: CustomFetchOptions,
    apiCallStartTime: number
  ): Promise<unknown> {
    if (finalOptions.responseType === 'blob') {
      const raw = await $fetch.raw<Blob>(finalUrl, finalOptions)
      const apiCallDuration = Math.round(performance.now() - apiCallStartTime)
      if (!finalOptions.metadata) finalOptions.metadata = {}
      finalOptions.metadata.apiCallDuration = apiCallDuration
      BaseService.handleResponse(raw._data, finalOptions)
      const headers: Record<string, string> = {}
      raw.headers?.forEach((value: string | string[], key: string) => {
        const str = typeof value === 'string' ? value : Array.isArray(value) ? value[0] : String(value)
        headers[key.toLowerCase()] = str
      })
      return { data: raw._data as Blob, headers }
    }

    const response = await $fetch(finalUrl, finalOptions)
    const apiCallDuration = Math.round(performance.now() - apiCallStartTime)
    if (!finalOptions.metadata) finalOptions.metadata = {}
    finalOptions.metadata.apiCallDuration = apiCallDuration
    return BaseService.handleResponse(response, finalOptions)
  }

  private static attachFailedFetchDuration(finalOptions: CustomFetchOptions, apiCallStartTime: number): void {
    const duration = apiCallStartTime > 0 ? Math.round(performance.now() - apiCallStartTime) : undefined
    if (!finalOptions.metadata) finalOptions.metadata = {}
    finalOptions.metadata.apiCallDuration = duration
  }

  /**
   * Create and configure fetch instance
   */
  private static createFetchInstance(): FetchInstance {
    const defaultHeaders = BaseService.getDefaultHeaders()

    // Use ofetch with custom configuration
    const instance: FetchInstance = async (url: string, options: CustomFetchOptions = {}) => {
      // Snapshot before any BaseService mutation: `prepareRequest` re-runs on the 401-retry pass
      // over this same mutated headers object, so checking `headers.Authorization` there would see
      // BaseService's own first-pass value instead of the caller's original intent.
      const hasCallerExplicitAuthHeader = Boolean(options.headers?.Authorization || options.headers?.authorization)

      const mergedOptions: CustomFetchOptions = {
        ...options,
        url, // Add URL to options for tracking
        method: options.method || 'GET', // Add method for tracking
        headers: {
          ...(Object.fromEntries(Object.entries(defaultHeaders).filter(([, value]) => value !== undefined)) as Record<string, string>),
          ...options.headers,
        },
        timeout: 30000,
        retry: 0,
      }

      if (!mergedOptions.metadata) {
        mergedOptions.metadata = {}
      }
      mergedOptions.metadata._hasCallerExplicitAuthHeader = hasCallerExplicitAuthHeader

      // Apply request middleware
      const preRequestStartTime = performance.now()
      let { finalUrl, finalOptions } = BaseService.prepareRequest(url, mergedOptions)
      if (!finalOptions.metadata) finalOptions.metadata = {}
      finalOptions.metadata.preRequestDuration = Math.round(performance.now() - preRequestStartTime)

      let apiCallStartTime = 0
      try {
        // Use shared fetch (available in Nuxt) with safety check
        if (typeof $fetch === 'undefined') {
          throw new Error('$fetch is not available. Make sure you are running in a Nuxt context.')
        }

        apiCallStartTime = performance.now()
        return await BaseService.executePreparedFetch(finalUrl, finalOptions, apiCallStartTime)
      } catch (error) {
        BaseService.attachFailedFetchDuration(finalOptions, apiCallStartTime)

        const { logger } = useLogger()
        const fetchError = BaseService.toFetchError(error)

        const logUrl = mergedOptions.url ?? url
        const is401 = fetchError.status === 401
        const alreadyRetried = !!finalOptions.metadata._401Retried
        const refreshPathBlocked = BaseService.isAuthRefreshRequestUrl(finalUrl)
        const ssrNuxtApp = finalOptions.metadata?._ssrNuxtApp as SSRNuxtAppLike | undefined
        // Courses admin preview: skip the learner refresh/logout cascade on 401 during an active
        // admin-preview session — there's no learner session to restore, and wiping tokens would
        // destroy the `acatv2` admin session (mirrors membership-hmi-preview `hasAdminV2PreviewSession`).
        const adminPreviewActive = hasAdminV2PreviewSession()
        // Client-only refresh: on SSR the server auth middleware performs a single proactive
        // refresh before render, so mid-render refreshes are disabled. They raced refresh-token
        // rotation and their `Set-Cookie` was dropped after streaming flushed headers — the
        // duplicate-call / logout-storm source. An SSR 401 simply surfaces to the page.
        const shouldAttemptRefresh = process.client && is401 && !alreadyRetried && !refreshPathBlocked && !adminPreviewActive
        let refreshOk = false
        if (shouldAttemptRefresh) {
          logger.warn('[BaseService] 401 received — attempting token refresh before retry', {
            labels: { context: 'base.service', url: logUrl },
          })
          refreshOk = await BaseService.tryRefreshAccessToken(logUrl, ssrNuxtApp)
          if (!refreshOk) {
            logger.warn('[BaseService] Token refresh failed; surfacing original 401 without retry', {
              labels: { context: 'base.service', url: logUrl },
            })
          }
        }
        const canRetry401 = shouldAttemptRefresh && refreshOk

        if (canRetry401) {
          finalOptions.metadata._401Retried = true
          const retryPrepareStart = performance.now()
          const retryPrepared = BaseService.prepareRequest(url, mergedOptions)
          if (!retryPrepared.finalOptions.metadata) retryPrepared.finalOptions.metadata = {}
          const prevPre = typeof finalOptions.metadata.preRequestDuration === 'number' ? finalOptions.metadata.preRequestDuration : 0
          retryPrepared.finalOptions.metadata.preRequestDuration = prevPre + Math.round(performance.now() - retryPrepareStart)

          apiCallStartTime = performance.now()
          try {
            const retryResult = await BaseService.executePreparedFetch(retryPrepared.finalUrl, retryPrepared.finalOptions, apiCallStartTime)
            if (process.client) {
              logger.info('API call succeeded after 401 refresh retry', {
                labels: {
                  context: 'BaseService.createFetchInstance',
                  urlPreview: String(retryPrepared.finalUrl).slice(0, 120),
                },
              })
            }
            return retryResult
          } catch (retryError) {
            BaseService.attachFailedFetchDuration(retryPrepared.finalOptions, apiCallStartTime)
            const re = BaseService.toFetchError(retryError)
            if (process.client) {
              logger.warn('API call failed after 401 refresh retry', {
                labels: {
                  context: 'BaseService.createFetchInstance',
                  urlPreview: String(retryPrepared.finalUrl).slice(0, 120),
                  httpStatus: re.status ?? 0,
                  messagePreview: String(re.message || '').slice(0, 120),
                },
              })
            }

            return BaseService.handleResponseError(null, re, retryPrepared.finalOptions)
          }
        }

        return BaseService.handleResponseError(null, fetchError, finalOptions)
      }
    }

    return instance
  }

  /**
   * Get default headers
   */
  private static getDefaultHeaders(): HTTPHeaders {
    const headers: HTTPHeaders = {
      channel: 'APP',
      source: 'PORTAL_USER',
      version: '2023-02-21',
      'x-app-version': 'web',
      'x-app-build': `communities-nuxt-${process.env.NUXT_ENV || 'development'}`,
    }

    // Add platform details if available
    if (process.client) {
      try {
        const platform = BaseService.getPlatformDetails()
        if (platform) {
          headers['x-platform-details'] = platform
        }
      } catch {
        // Ignore platform detection errors
      }
    }

    return headers
  }

  /**
   * Check if token should be skipped for specific endpoints
   */
  private static shouldSkipToken(url: string): boolean {
    const skipEndpoints = ['portal-settings', '/communities/analytics/track-event']

    return skipEndpoints.some(endpoint => url.includes(endpoint))
  }

  /**
   * URLs that must not trigger 401→refresh (avoids infinite loops and deadlocks).
   *
   * - **Refresh endpoint**: Retrying refresh with another refresh is nonsensical.
   * - **Session endpoints** (`/auth/session`, `/v2/.../auth/session/...`): `useAuth.logout()`
   *   calls `updateSession` before clearing cookies. If that request 401s, `tryRefreshAccessToken`
   *   would await `_refreshInFlight` while `_refreshInFlight` is already waiting on
   *   `refreshToken()` → `logout()` → same `updateSession`, deadlocking the client.
   */
  private static isAuthRefreshRequestUrl(url: string): boolean {
    const lower = url.toLowerCase()
    return lower.includes('/auth/refresh-token') || lower.includes('/auth/refreshtoken') || lower.includes('/auth/session')
  }

  /**
   * Refresh access token via useAuth (Pinia auth store).
   *
   * Client-side only: `createFetchInstance` never calls this on SSR (the server auth
   * middleware owns proactive refresh before render). The `ssrNuxtApp` fallback remains
   * for legacy callers but is no longer exercised from the 401 path.
   *
   * Dedup: concurrent 401s share a single in-flight refresh promise scoped to
   * the current Nuxt app instance (per-request on SSR, singleton on client).
   * This avoids firing N parallel /auth/refresh-token calls when N requests
   * race and all receive 401, and prevents last-writer-wins races on cookies
   * and the auth store.
   *
   * @returns true when a new token was applied and the original request may be retried.
   */
  private static async tryRefreshAccessToken(requestUrlForLog: string, ssrNuxtApp?: SSRNuxtAppLike): Promise<boolean> {
    type RefreshableNuxtApp = (ReturnType<typeof useNuxtApp> & { _refreshInFlight?: Promise<boolean> }) | SSRNuxtAppLike
    let nuxtApp: RefreshableNuxtApp | undefined
    let pinia: Pinia | undefined
    try {
      const ambient = useNuxtApp() as ReturnType<typeof useNuxtApp> & { _refreshInFlight?: Promise<boolean> }
      nuxtApp = ambient
      pinia = ambient.$pinia as Pinia
    } catch {
      // Fall back to an SSR-provided nuxtApp when the ambient context is not reachable
      // (nitro handlers called outside composable setup). Dedup still works because
      // `_refreshInFlight` is shared on the passed-in nuxtApp instance for that request.
      if (ssrNuxtApp) {
        nuxtApp = ssrNuxtApp
        pinia = ssrNuxtApp.$pinia
      } else {
        const { logger } = useLogger()
        logger.warn('Not in Nuxt context for token refresh, skipping', {
          labels: {
            context: 'BaseService.tryRefreshAccessToken',
            url: requestUrlForLog,
          },
        })
        return false
      }
    }

    // Reuse an in-flight refresh from a concurrent 401 on the same Nuxt app instance.
    if (nuxtApp._refreshInFlight) {
      return nuxtApp._refreshInFlight
    }

    const inFlight = (async (): Promise<boolean> => {
      const { logger } = useLogger()
      const { useAuth: getAuthComposable } = await import('../../auth-layer/composables/useAuth')
      const auth = getAuthComposable(pinia)

      // Thread the SSR event (if we have one) straight into useAuth.refreshToken so it can
      // read `event.context.auth` — set by the server auth middleware — without re-calling
      // `useNuxtApp()`, which throws inside nitro handler call stacks.
      const ssrEvent = ssrNuxtApp?.ssrContext?.event

      try {
        logger.info('Refreshing token', {
          labels: {
            context: 'BaseService.tryRefreshAccessToken',
            url: requestUrlForLog,
          },
        })

        if (!auth.refreshToken) {
          logger.warn('Token refresh skipped: refreshToken unavailable on auth composable', {
            labels: { context: 'BaseService.tryRefreshAccessToken', url: requestUrlForLog },
          })
          return false
        }

        // `previousReq` is diagnostic-only: it lets a resulting auto-logout name the request
        // whose 401 started this refresh. `ssrEvent` keeps its existing conditional shape.
        const result = await auth.refreshToken({
          ...(ssrEvent ? { ssrEvent } : {}),
          previousReq: { url: sanitizeAutoLogoutRequestUrl(requestUrlForLog), statusCode: 401 },
        })
        if (result.success) {
          logger.info('Token refreshed successfully', {
            labels: {
              context: 'BaseService.tryRefreshAccessToken',
              url: requestUrlForLog,
            },
          })
          return true
        }

        logger.error('Token refresh failed', {
          labels: {
            context: 'BaseService.tryRefreshAccessToken',
            url: requestUrlForLog,
            reason: result.error ?? 'unknown',
          },
        })
        return false
      } catch (refreshError) {
        logger.error('Token refresh threw', {
          labels: { context: 'BaseService.tryRefreshAccessToken', url: requestUrlForLog },
          error: refreshError instanceof Error ? refreshError : new Error(String(refreshError)),
        })
        return false
      }
    })()

    nuxtApp._refreshInFlight = inFlight
    try {
      return await inFlight
    } finally {
      // Clear only if this run is still the active one; next 401 can refresh again.
      if (nuxtApp._refreshInFlight === inFlight) {
        nuxtApp._refreshInFlight = undefined
      }
    }
  }

  /**
   * Get platform details for headers
   */
  private static getPlatformDetails(): string | null {
    if (!process.client) return null

    try {
      const userAgent = navigator.userAgent
      const platform = navigator.platform

      if (userAgent.includes('Android')) return 'android'
      if (userAgent.includes('iPhone') || userAgent.includes('iPad')) return 'ios'
      if (userAgent.includes('Windows')) return 'windows'
      if (userAgent.includes('Mac')) return 'mac'
      if (userAgent.includes('Linux')) return 'linux'

      return platform || 'unknown'
    } catch {
      return null
    }
  }

  /**
   * Handle GoKollab specific request modifications
   */
  private static handleGoKollabRequests(
    request: string,
    options: CustomFetchOptions
  ): { finalUrl: string; finalOptions: CustomFetchOptions } {
    try {
      const { isGokollab } = useGlobal()

      if (!isGokollab) {
        return { finalUrl: request, finalOptions: options }
      }
    } catch (error) {
      // If we can't get the global state, assume not GoKollab
      const { logger } = useLogger()
      logger.warn('Could not access global state', {
        labels: {
          context: 'BaseService.handleGoKollabRequests',
          requestUrl: request,
        },
        error: error instanceof Error ? error : new Error(String(error)),
      })
      return { finalUrl: request, finalOptions: options }
    }

    // Check if the URL already contains /gokollab to prevent infinite replacements
    if (request.includes('/gokollab/')) {
      return { finalUrl: request, finalOptions: options }
    }

    let modifiedRequest = request
    const modifiedOptions = { ...options }

    if (!modifiedOptions.headers) {
      modifiedOptions.headers = {}
    }

    // Handle communities requests
    if (request.includes('/communities/')) {
      modifiedRequest = request.replace('/communities/', '/gokollab/communities/')
      if (modifiedOptions.baseURL) {
        modifiedOptions.baseURL = modifiedOptions.baseURL.replace('/communities', '/gokollab/communities')
      }
    }

    // Handle community-analytics requests (WL/direct CA path shape)
    if (request.includes('/community-analytics/')) {
      modifiedRequest = request.replace('/community-analytics/', '/gokollab/community-analytics/')
      if (modifiedOptions.baseURL) {
        modifiedOptions.baseURL = modifiedOptions.baseURL.replace('/community-analytics', '/gokollab/community-analytics')
      }
    }

    if (request.includes('/clientclub/leaderboards/')) {
      modifiedRequest = request.replace('/clientclub/leaderboards/', '/gokollab/clientclub/leaderboards/')
      if (modifiedOptions.baseURL) {
        modifiedOptions.baseURL = modifiedOptions.baseURL.replace('/clientclub/leaderboards', '/gokollab/clientclub/leaderboards')
      }
    }

    if (request.includes('/clientclub/') && request.includes('/events/')) {
      modifiedRequest = request.replace('/clientclub/', '/gokollab/clientclub/')
      if (modifiedOptions.baseURL) {
        modifiedOptions.baseURL = modifiedOptions.baseURL.replace('/clientclub/', '/gokollab/clientclub/')
      }
    }

    // Handle CRM contact requests (e.g. PATCH /{locationId}/users/crm-contact/{contactId}).
    // The leading path segment is the dynamic locationId, so we can't anchor with a
    // simple .replace() like the families above — insert /gokollab right after the host.
    if (request.includes('/users/crm-contact/')) {
      modifiedRequest = request.replace(/(\/\/[^/]+)\//, '$1/gokollab/')
      if (modifiedOptions.baseURL && !modifiedOptions.baseURL.includes('/gokollab')) {
        modifiedOptions.baseURL = `${modifiedOptions.baseURL.replace(/\/$/, '')}/gokollab`
      }
    }

    // Handle membership requests
    if (request.includes('/memberships/')) {
      modifiedRequest = request.replace('/memberships/', '/gokollab/memberships/')
      if (modifiedOptions.baseURL) {
        modifiedOptions.baseURL = modifiedOptions.baseURL.replace('/memberships', '/gokollab/memberships')
      }
    }

    // Handle membership requests
    else if (request.includes('/membership/')) {
      modifiedRequest = request.replace('/membership/', '/gokollab/memberships/')
      if (modifiedOptions.baseURL) {
        modifiedOptions.baseURL = modifiedOptions.baseURL.replace('/membership', '/gokollab/memberships')
      }
    }

    // Handle courses learners requests
    if (request.includes('/courses/learners')) {
      modifiedRequest = request.replace('/courses/learners', '/gokollab/courses/learners')
      if (modifiedOptions.baseURL) {
        modifiedOptions.baseURL = modifiedOptions.baseURL.replace('/courses/learners', '/gokollab/courses/learners')
      }
    }

    // Handle certificates requests
    if (request.includes('/certificates/')) {
      modifiedRequest = request.replace('/certificates/', '/gokollab/certificates/')
      if (modifiedOptions.baseURL) {
        modifiedOptions.baseURL = modifiedOptions.baseURL.replace('/certificates', '/gokollab/certificates')
      }
    }

    // handle assets-drm requests
    if (request.includes('/assets-drm/')) {
      modifiedRequest = request.replace('/assets-drm/', '/gokollab/assets-drm/')
      if (modifiedOptions.baseURL) {
        modifiedOptions.baseURL = modifiedOptions.baseURL.replace('/assets-drm', '/gokollab/assets-drm')
      }
    }

    // Update the request URL in options for logging consistency
    modifiedOptions.url = modifiedRequest

    return { finalUrl: modifiedRequest, finalOptions: modifiedOptions }
  }

  /**
   * Meet app: strip /v2/ for auth (-> /auth/*) and for clientclub (clientclub/v2/ -> clientclub/).
   */
  private static handleMeetAppRequest(
    request: string,
    options: CustomFetchOptions
  ): { finalUrl: string; finalOptions: CustomFetchOptions } {
    try {
      const modifiedOptions = { ...options }
      let modifiedRequest = request
      const locationStore = useLocationStore()
      const locationId = locationStore.locationId
      const { isAuthV2Enabled } = useLocationStore()
      if (isAuthV2Enabled) {
        return BaseService.handleClientPortalRequests(request, options)
      }
      // Signup: /v2/:locationId/auth/signup -> /v2/:locationId/users
      if (request.includes('/auth/signup')) {
        modifiedRequest = modifiedRequest.replace(/\/auth\/signup/, '/users')
      }
      // OTP send: /otps/:locationId/send -> /auth/login/otp
      else if (request.includes('/otps/') && request.includes('/send')) {
        modifiedRequest = modifiedRequest.replace(/\/otps\/[^/]+\/send/, '/auth/login/otp')
      } else if (request.includes('/auth/set-password')) {
        modifiedRequest = modifiedRequest.replace(/\/auth\/set-password/, `/${locationId}/users/set-password`)
      }
      // Auth: /v2/:locationId/auth/* -> /auth/*
      else if (request.includes('/auth/') || request.includes('/otps/')) {
        modifiedRequest = modifiedRequest.replace(/\/v2\/[^/]+\/auth/, '/auth')
      }

      // Clientclub: clientclub/v2/:locationId/... -> clientclub/:locationId/... (e.g. users/...)
      if (request.includes('clientclub/v2/')) {
        modifiedRequest = modifiedRequest.replace(/clientclub\/v2\//, 'clientclub/')
      }

      modifiedOptions.url = modifiedRequest
      return { finalUrl: modifiedRequest, finalOptions: modifiedOptions }
    } catch (error) {
      const { logger } = useLogger()
      logger.warn('Error in handleMeetAppRequest', {
        labels: {
          context: 'BaseService.handleMeetAppRequest',
          requestUrl: request,
        },
        error: error instanceof Error ? error : new Error(String(error)),
      })
      return { finalUrl: request, finalOptions: options }
    }
  }

  private static handleClientPortalRequests(
    request: string,
    options: CustomFetchOptions
  ): { finalUrl: string; finalOptions: CustomFetchOptions } {
    try {
      // Short-circuit if the URL has already been routed through the clientportal-middleware
      if (request.includes('/clientportal-middleware')) {
        return { finalUrl: request, finalOptions: options }
      }

      // Explicit opt-out for endpoints that must bypass middleware rewriting.
      if (options.skipClientPortalMiddleware) {
        return { finalUrl: request, finalOptions: options }
      }

      let modifiedRequest = request
      const modifiedOptions = { ...options }

      if (!modifiedOptions.headers) {
        modifiedOptions.headers = {}
      }

      // Handle clientclub requests
      if (request.includes('/clientclub/')) {
        modifiedRequest = request.replace('/clientclub/', '/clientportal-middleware/clientclub/')
        if (modifiedOptions.baseURL) {
          modifiedOptions.baseURL = modifiedOptions.baseURL.replace('/clientclub', '/clientportal-middleware/clientclub')
        }
      }

      if (request.includes('/communities/')) {
        modifiedRequest = request.replace('/communities/', '/clientportal-middleware/communities/')
        if (modifiedOptions.baseURL) {
          modifiedOptions.baseURL = modifiedOptions.baseURL.replace('/communities', '/clientportal-middleware/communities')
        }
      }

      // Handle membership requests
      if (modifiedRequest.includes('/memberships/')) {
        modifiedRequest = modifiedRequest.replace('/memberships/', '/clientportal-middleware/memberships/')
        if (modifiedOptions.baseURL) {
          modifiedOptions.baseURL = modifiedOptions.baseURL.replace('/memberships', '/clientportal-middleware/memberships')
        }
      }

      // Handle singular /membership/ requests
      else if (modifiedRequest.includes('/membership/')) {
        modifiedRequest = modifiedRequest.replace('/membership/', '/clientportal-middleware/memberships/')
        if (modifiedOptions.baseURL) {
          modifiedOptions.baseURL = modifiedOptions.baseURL.replace('/membership', '/clientportal-middleware/memberships')
        }
      }

      // Handle courses learners requests
      if (modifiedRequest.includes('/courses/learners')) {
        modifiedRequest = modifiedRequest.replace('/courses/learners', '/clientportal-middleware/courses/learners')
        if (modifiedOptions.baseURL) {
          modifiedOptions.baseURL = modifiedOptions.baseURL.replace('/courses/learners', '/clientportal-middleware/courses/learners')
        }
      }

      // handle assets-drm requests
      if (modifiedRequest.includes('/assets-drm/')) {
        modifiedRequest = modifiedRequest.replace('/assets-drm/', '/clientportal-middleware/assets-drm/')
        if (modifiedOptions.baseURL) {
          modifiedOptions.baseURL = modifiedOptions.baseURL.replace('/assets-drm', '/clientportal-middleware/assets-drm')
        }
      }

      // Handle certificates requests (account-settings certifications + courses certificate layers)
      if (modifiedRequest.includes('/certificates/')) {
        modifiedRequest = modifiedRequest.replace('/certificates/', '/clientportal-middleware/certificates/')
        if (modifiedOptions.baseURL) {
          modifiedOptions.baseURL = modifiedOptions.baseURL.replace('/certificates', '/clientportal-middleware/certificates')
        }
      }

      // Update the request URL in options for logging consistency
      modifiedOptions.url = modifiedRequest

      return { finalUrl: modifiedRequest, finalOptions: modifiedOptions }
    } catch (error) {
      const { logger } = useLogger()
      logger.warn('Error in handleClientPortalRequests', {
        labels: {
          context: 'BaseService.handleClientPortalRequests',
          requestUrl: request,
        },
        error: error instanceof Error ? error : new Error(String(error)),
      })
      return { finalUrl: request, finalOptions: options }
    }
  }

  /**
   * Get the full URL by combining base URL and base path.
   * @param skipBasePath - When true, only baseURL + endpoint is used (for endpoints not under basePath, e.g. /documents/download/:id).
   */
  protected getFullURL(endpoint: string = '', skipBasePath = false): string {
    const { baseURL, basePath = '' } = this.config
    const config = getConfig()
    // Get base URL from config or use as-is if it's a full URL
    let resolvedBaseURL: string
    if (baseURL && baseURL in config) {
      resolvedBaseURL = config[baseURL as keyof typeof config] as string
    } else {
      resolvedBaseURL = baseURL || config.REST_API_URLS
    }

    // Ensure no double slashes
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`.replace(/\/+/g, '/')
    resolvedBaseURL = resolvedBaseURL.replace(/\/$/, '')

    if (skipBasePath) {
      return `${resolvedBaseURL}${cleanEndpoint}`
    }

    const cleanBasePath = basePath.startsWith('/') ? basePath : `/${basePath}`
    const fullPath = `${cleanBasePath}${cleanEndpoint}`.replace(/\/+/g, '/')
    return `${resolvedBaseURL}${fullPath}`
  }

  /**
   * HTTP methods with automatic URL resolution
   * The response type depends on the TResponse generic parameter of the class
   * Maintains axios-like response structure for compatibility
   */
  protected async get<T = unknown>(
    endpoint: string,
    config?: CustomFetchOptions
  ): Promise<{
    data: T
    status: number
    statusText: string
    headers: Record<string, string>
    config: CustomFetchOptions
  }> {
    const url = this.getFullURL(endpoint)
    const options = this.mergeConfig(config)

    try {
      const response = await this.http(url, {
        method: 'GET',
        ...options,
      })

      // Return axios-like response structure
      return {
        data: response as T,
        status: 200, // ofetch doesn't expose status in success case
        statusText: 'OK',
        headers: {},
        config: options || {},
      }
    } catch (error) {
      this.logApiError('get', endpoint, error, {
        params: options?.params,
      })
      throw BaseService.preserveHttpError(error)
    }
  }

  /**
   * GET request that returns blob and response headers
   */
  protected async getBlob(endpoint: string, config?: CustomFetchOptions): Promise<{ data: Blob; headers: Record<string, string> }> {
    const url = this.getFullURL(endpoint, config?.skipBasePath === true)
    const options = this.mergeConfig({ ...config, method: 'GET', responseType: 'blob' })

    try {
      const result = await this.http(url, options ?? {})
      return result as { data: Blob; headers: Record<string, string> }
    } catch (error) {
      this.logApiError('getBlob', endpoint, error, { params: options?.params })
      throw error
    }
  }

  /**
   * POST request that returns blob and response headers
   */
  protected async postBlob(
    endpoint: string,
    data?: unknown,
    config?: CustomFetchOptions
  ): Promise<{ data: Blob; headers: Record<string, string> }> {
    const url = this.getFullURL(endpoint)
    const options = this.mergeConfig({ ...config, method: 'POST', responseType: 'blob', body: data as RequestBody })

    try {
      const result = await this.http(url, options ?? {})
      return result as { data: Blob; headers: Record<string, string> }
    } catch (error) {
      this.logApiError('postBlob', endpoint, error, { body: data, ...(options?.params && { params: options.params }) })
      throw error
    }
  }

  protected async post<T = unknown>(
    endpoint: string,
    data?: unknown,
    config?: CustomFetchOptions
  ): Promise<{
    data: T
    status: number
    statusText: string
    headers: Record<string, string>
    config: CustomFetchOptions
  }> {
    const url = this.getFullURL(endpoint)
    const options = this.mergeConfig(config)

    try {
      const response = await this.http(url, {
        method: 'POST',
        body: data as RequestBody,
        ...options,
      })

      return {
        data: response as T,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: options || {},
      }
    } catch (error) {
      this.logApiError('post', endpoint, error, {
        body: data,
        ...(options?.params && { params: options.params }),
      })
      throw BaseService.preserveHttpError(error)
    }
  }

  protected async put<T = unknown>(
    endpoint: string,
    data?: unknown,
    config?: CustomFetchOptions
  ): Promise<{
    data: T
    status: number
    statusText: string
    headers: Record<string, string>
    config: CustomFetchOptions
  }> {
    const url = this.getFullURL(endpoint)
    const options = this.mergeConfig(config)

    try {
      const response = await this.http(url, {
        method: 'PUT',
        body: data as RequestBody,
        ...options,
      })

      return {
        data: response as T,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: options || {},
      }
    } catch (error) {
      this.logApiError('put', endpoint, error, {
        body: data,
        ...(options?.params && { params: options.params }),
      })
      throw BaseService.preserveHttpError(error)
    }
  }

  protected async patch<T = unknown>(
    endpoint: string,
    data?: unknown,
    config?: CustomFetchOptions
  ): Promise<{
    data: T
    status: number
    statusText: string
    headers: Record<string, string>
    config: CustomFetchOptions
  }> {
    const url = this.getFullURL(endpoint)
    const options = this.mergeConfig(config)

    try {
      const response = await this.http(url, {
        method: 'PATCH',
        body: data as RequestBody,
        ...options,
      })

      return {
        data: response as T,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: options || {},
      }
    } catch (error) {
      this.logApiError('patch', endpoint, error, {
        body: data,
        ...(options?.params && { params: options.params }),
      })
      throw BaseService.preserveHttpError(error)
    }
  }

  protected async delete<T = unknown>(
    endpoint: string,
    config?: CustomFetchOptions
  ): Promise<{
    data: T
    status: number
    statusText: string
    headers: Record<string, string>
    config: CustomFetchOptions
  }> {
    const url = this.getFullURL(endpoint)
    const options = this.mergeConfig(config)

    try {
      const response = await this.http(url, {
        method: 'DELETE',
        ...options,
      })

      return {
        data: response as T,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: options || {},
      }
    } catch (error) {
      this.logApiError('delete', endpoint, error, {
        ...(options?.params && { params: options.params }),
      })
      throw BaseService.preserveHttpError(error)
    }
  }

  /**
   * Get service name for logging (avoids minified constructor.name in production)
   */
  private getServiceName(): string {
    // Use explicit serviceName from config if provided
    if (this.config.serviceName) {
      return this.config.serviceName
    }
    // Fallback to constructor.name (may be minified in production)
    return this.constructor.name
  }

  /**
   * Log API error with structured information
   */
  private logApiError(method: string, endpoint: string, error: unknown, requestData?: Record<string, unknown>): void {
    const fetchError = error as FetchError
    const apiRequest: Record<string, unknown> = requestData || {}

    // Add params for GET requests
    if (requestData && 'params' in requestData) {
      apiRequest.params = requestData.params
    }

    // Add body for POST/PUT/PATCH requests
    if (requestData && 'body' in requestData) {
      apiRequest.body = requestData.body
    }

    // Extract proper error message instead of converting object to string
    const errorMessage = BaseService.extractErrorMessage(error)
    const errorForLogging = error instanceof Error ? error : new Error(errorMessage)

    const serviceName = this.getServiceName()
    this.logger.error(`[${serviceName}.${method}] Error in API call`, {
      labels: {
        method,
        endpoint,
      },
      error: errorForLogging,
      payload: {
        statusCode: fetchError.status || fetchError.data?.statusCode,
        apiResponse: fetchError.data,
        apiRequest: Object.keys(apiRequest).length > 0 ? apiRequest : undefined,
      },
    })
  }

  private static preserveHttpError(error: unknown): Error {
    const message = BaseService.extractErrorMessage(error)
    const fetchErr = error as Partial<FetchError>
    const statusFromResponse = typeof fetchErr.status === 'number' ? fetchErr.status : undefined
    const statusFromBody = typeof fetchErr.data?.statusCode === 'number' ? fetchErr.data.statusCode : undefined
    const status = statusFromResponse ?? statusFromBody

    if (typeof status === 'number') {
      const payload = fetchErr.data
      const err = new Error(message) as Error & {
        status: number
        response: {
          status: number
          headers?: Record<string, string>
          data?: FetchError['data']
        }
        /** Same object as `response.data` — mirrors FetchError top-level shape for older callers. */
        data?: FetchError['data']
      }
      err.status = status
      const responseHeaders = fetchErr.response?.headers
      err.response = {
        status,
        ...(responseHeaders ? { headers: responseHeaders } : {}),
        ...(payload !== undefined ? { data: payload } : {}),
      }
      if (payload !== undefined) {
        err.data = payload
      }
      return err
    }

    return error instanceof Error ? error : new Error(message)
  }

  /**
   * Safely extract error message from various error types
   */
  private static extractErrorMessage(error: unknown): string {
    // Handle FetchError type
    if (error && typeof error === 'object' && 'data' in error) {
      const fetchError = error as FetchError
      if (fetchError.data?.message) {
        return fetchError.data.message
      }
      if (fetchError.message) {
        return fetchError.message
      }
    }

    // Handle standard Error type
    if (error instanceof Error) {
      return error.message
    }

    // Handle string errors
    if (typeof error === 'string') {
      return error
    }

    // Handle objects with message property
    if (error && typeof error === 'object' && 'message' in error) {
      const message = (error as { message: unknown }).message
      if (typeof message === 'string') {
        return message
      }
    }

    // Fallback for unknown error types
    return 'An unexpected error occurred'
  }

  /**
   * Merge service-level config with request-level config
   */
  private mergeConfig(requestConfig?: CustomFetchOptions): CustomFetchOptions | undefined {
    if (!this.config.headers && !requestConfig) {
      return requestConfig
    }

    const { groupId, locationId, ...fetchConfig } = requestConfig || {}

    const mergedConfig = {
      ...fetchConfig,
      headers: {
        ...this.config.headers,
        ...fetchConfig?.headers,
      },
      // Store groupId and locationId in metadata for the interceptor
      metadata: {
        groupId,
        locationId,
        ...fetchConfig?.metadata,
      },
    }

    return mergedConfig
  }

  /**
   * Test API stats functionality
   * Useful for testing the logging format
   */
  public static async testApiStats(): Promise<void> {
    const { testLatencyLogging } = await import('../utils/api-stats.utils')
    testLatencyLogging()
  }

  /**
   * Test API stats with a mock request
   * Useful for testing the complete flow
   */
  public static testApiStatsWithMockRequest(): void {
    const { logger } = useLogger()
    logger.info('Testing API stats with mock request', {
      labels: {
        context: 'BaseService.testApiStatsWithMockRequest',
      },
    })

    // Create a mock config with metadata
    const mockConfig = {
      url: 'https://api.example.com/test',
      method: 'GET',
      metadata: {
        startTime: performance.now(),
        requestId: 'test_req_' + Date.now(),
      },
    }

    // Simulate a successful request
    trackApiLatency(mockConfig, 200, null)

    // Simulate a failed request
    const mockConfig2 = {
      url: 'https://api.example.com/test-error',
      method: 'POST',
      metadata: {
        startTime: performance.now() - 1000,
        requestId: 'test_req_error_' + Date.now(),
      },
    }

    trackApiLatency(mockConfig2, 500, 'Internal Server Error')

    logger.info('Mock API stats test completed', {
      labels: {
        context: 'BaseService.testApiStatsWithMockRequest',
      },
    })
  }
}
